'use client';

import { Fragment } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronRight, Plus } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';

/**
 * The two things a chat bubble can carry besides words.
 *
 * ── Why they live INSIDE the bubble ─────────────────────────────────────────
 *
 * Every non-bubble thing in this chat used to be its own container: the staged
 * approval card, the help card, the skill install card, the delete
 * confirmation, the document link, the nav button, the mutation pill. Eleven
 * elements, seven different silhouettes — chamfers at three notches, the
 * dovetail button, and border-radius at 7px, 8px and 10px — built across four
 * eras by people who were not designing the same surface. A single thread could
 * show four of them inside three replies of each other.
 *
 * They are now strips inside the bubble, divided off by a hairline. The bubble
 * is the only container in the chat, and the ordering is always the same:
 * body, then rows, then actions, each of them optional.
 *
 * ── The two rules ───────────────────────────────────────────────────────────
 *
 * 1. Anything Mucka HANDS you is in her bubble.
 * 2. A chevron means it goes somewhere; a bare row answers her.
 *
 * That second one is what stops navigation being a wall of orange CTAs —
 * `JobListCard`'s own comment has complained about exactly that since it was
 * written ("six of those is a wall of orange calls-to-action rather than a
 * list"). A destination is a row now, and orange is reserved for a tap that
 * sends or changes something.
 *
 * ── One component, both surfaces ────────────────────────────────────────────
 *
 * MuckaPanel and the Mucka Bar import this same file. They have drifted every
 * single time they were allowed to be two: the approval card did not exist in
 * the bar AT ALL until TKT-1325, which is four owners over one day being told
 * to tap a button that was never on any screen they had open.
 */

/**
 * Where a staged approval is in its one decision.
 *
 * Lives here rather than in MuckaPanel because the bar reads it too — the bar
 * asks, the panel decides, and both draw off the same value so one copy of a
 * card cannot sit on a live Send button while the other says it has gone.
 */
export type StagedState = 'idle' | 'approving' | 'approved' | 'dismissing' | 'dismissed' | 'error';

/** How loudly an action speaks. `primary` is reserved for a tap that sends or
 *  changes something — never for going somewhere. */
export type BubbleActionTone = 'primary' | 'quiet' | 'destructive';

export interface BubbleAction {
  label: string;
  onPress: () => void;
  tone?: BubbleActionTone;
  /** Shown in place of `label` while the tap is in flight. */
  busyLabel?: string;
  busy?: boolean;
  disabled?: boolean;
}

/**
 * What a row is for, which is the whole of its appearance.
 *
 * `go` carries a chevron and opens something. `answer` is bare and sends that
 * text back to Mucka. `more` is the dimmed "one of the others" row that closes
 * a capped list — a plus rather than a chevron, so it reads as *there are more*
 * and not as a fourth Smith.
 */
export type BubbleRowKind = 'go' | 'answer' | 'more';

export interface BubbleRow {
  id: string;
  title: string;
  /** The small print under the title. This is what a chip could never hold:
   *  "Spencer" and "Kaur" were never the question — the address is. */
  detail?: string;
  /** Right-hand column — a time, a job type. Never the thing being named. */
  meta?: string;
  icon?: LucideIcon;
  kind?: BubbleRowKind;
  /**
   * Section heading, drawn above this row when it differs from the one before.
   *
   * Carried on the ROW rather than passed as a grouped structure because
   * `job-list-rows.ts` reads it off the database and this component chooses no
   * wording — which is the contract that keeps the fourth row reading exactly
   * like the first. Null on an ungrouped list.
   */
  group?: string | null;
  onPress: () => void;
}

/**
 * Rows inside a bubble — a day's jobs, two help articles, which Danny he means.
 *
 * Deliberately one component for all three: a list of things to open and a list
 * of things to pick between differ by a chevron, and building them separately
 * is how the surface ended up with eleven elements in the first place.
 */
export function BubbleRows({ rows, label }: {
  rows: readonly BubbleRow[];
  /** Screen-reader name for the list, e.g. "3 jobs". */
  label?: string;
}) {
  if (!rows.length) return null;
  return (
    <div role="list" aria-label={label}>
      {rows.map((row, i) => {
        const kind = row.kind ?? 'go';
        return (
          <Fragment key={row.id}>
            {row.group && row.group !== rows[i - 1]?.group && (
              <div className="mchat-group" role="presentation">{row.group}</div>
            )}
            <button
              type="button"
              role="listitem"
              className={`mchat-row${kind === 'more' ? ' mchat-row--more' : ''}`}
              onClick={row.onPress}
            >
              {row.icon && <Icon icon={row.icon} size={15} className="mchat-row-icon" />}
              <span className="mchat-row-main">
                <span className="mchat-row-title">{row.title}</span>
                {row.detail && <span className="mchat-row-detail">{row.detail}</span>}
              </span>
              {row.meta && <span className="mchat-row-meta">{row.meta}</span>}
              {kind !== 'answer' && (
                <Icon icon={kind === 'more' ? Plus : ChevronRight} size={15} className="mchat-row-go" />
              )}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * The action strip at the bottom of a bubble — approve, decline, try again.
 *
 * 🚨 Two at most. A third means the block is really a page, and a decision with
 * three answers is a question with rows.
 *
 * `done` replaces the whole strip with a receipt rather than removing it: the
 * decision STAYS in the transcript, so scrolling back in a month still shows
 * what was agreed and what it said. A strip that vanished would leave the
 * owner's own words above an empty space.
 */
export function BubbleActions({ actions, done }: {
  actions: readonly BubbleAction[];
  /** Past tense, e.g. "Sent, 11:42". Renders instead of the actions. */
  done?: string | null;
}) {
  if (done) {
    return (
      <div className="mchat-actions mchat-actions--done">
        <span className="mchat-done">
          <Icon icon={Check} size={14} />
          {done}
        </span>
      </div>
    );
  }
  if (!actions.length) return null;
  return (
    <div className="mchat-actions">
      {actions.slice(0, 2).map((action, i) => (
        <span className="mchat-action-cell" key={action.label}>
          {i > 0 && <span className="mchat-action-rule" aria-hidden="true" />}
          <button
            type="button"
            className={`mchat-action mchat-action--${action.tone ?? 'primary'}`}
            disabled={action.busy || action.disabled}
            onClick={action.onPress}
          >
            {action.busy && action.busyLabel ? action.busyLabel : action.label}
          </button>
        </span>
      ))}
    </div>
  );
}
